import { loadJsZip } from "@/components/appStart/loader";
import {
  getGeometryFromStl,
  getGeometryFromGlb,
} from "@/components/geometry/geometryLoader.js";
import {
  initializeControls,
  treatmentContext,
  clearOrtoAccesories,
  showLoader,
  hideLoader,
  findTeethCollisions,
  findTeethAnchorageLoss,
} from "@/components/ortho/OrthoPlanMode.js";
import { switchStitching } from "@/components/appStart/stitching";

//#region fields

const PRE_TREATMENT_TAG = "PRE_TREATMENT";
const ANIMATE_STAGE_MS = 2000;

let originalCbctTeeth = null;
let originalStitchedTeeth = null;
let originalTeeth = null;
let playInitialized = false;
let playInterval = null;
let currentStageId = 0;
let collisionsFound = false;

//#endregion

//#region load json

async function fetchTreatmentStages(stagesData) {
  let isMultiTreatment = false;
  let treatmentStages = [];
  let patientIds = [];
  let stageModels = [];

  console.log("fetchTreatmentStages", stagesData);
  let index = 0;
  for (let st of stagesData) {
    let patientId = st.patientId;
    if (!patientIds.includes(patientId)) {
      patientIds.push(patientId);
      let stageUrl = decodeURIComponent(st.jsonLink);
      let stage = {
        id: index,
        patientId: patientId,
        name: st.stageName,
        jsonLink: st.jsonLink,
        url: stageUrl,
        isOriginal: false,
        isCurrent: false,
        isStitched: false,
      };
      index++;
      if (!vueApp.originalTreatmentStage) {
        if (st.isOriginal !== undefined) {
          stage.isOriginal = st.isOriginal;
        } else {
          stage.isOriginal = st.stageName.includes(PRE_TREATMENT_TAG);
        }
        if (stage.isOriginal) {
          vueApp.originalTreatmentStage = stage;
        }
      }

      if (patientId == vueApp.patientId) {
        stage.disabled = true;
        stage.name += " (CURRENT)";
        stage.isCurrent = true;
        vueApp.currentTreatmentStage = stage;
      } else {
        isMultiTreatment = true;
        stage.isCurrent = false;
      }
      treatmentStages.push(stage);
    }
  }

  if (isMultiTreatment) {
    vueApp.treatmentStages = treatmentStages;
    vueApp.isValidTreatment = true;
    for (const stage of treatmentStages) {
      setStageLinks(stage);
      stage.isValid = await loadStageMatrices(stage);
      if (!stage.isValid) {
        vueApp.isValidTreatment = false;
      }
    }
    console.log("treatmentStages", vueApp.treatmentStages);

    if (!vueApp.currentTreatmentStage.isOriginal) {
      stageModels = await loadOriginalStageModels();
    }
  }
  return stageModels;
}

function setStageLinks(stage) {
  try {
    let jsonLocation = stage.jsonLink;
    let arr = jsonLocation.split("/");
    arr.pop();
    stage.s3dicom = arr[arr.length - 1];
    // stage.stlBaseUrl = arr.join("/");
    stage.stlBaseZip = arr.concat(["mobile.zip"]).join("/");

    const index = arr.indexOf("STL");
    arr.splice(index, 1);
    // stage.s3Url = arr.slice(0, arr.length - 1).join('/');
    stage.dicomUrl = arr.join("/");
    stage.crownCompositionUrl = arr.concat(["crown-composition"]).join("/");
    stage.crownMovementUrl = arr.concat(["crown-movement"]).join("/");

    arr.splice(index + 1, 0, "stlCopy");
    // stage.stlCopyUrl = arr.join("/");
    stage.stlCopyZip = arr.concat(["mobile.zip"]).join("/");
  } catch (error) {
    console.log("setStageLinks error", error);
  }
}

async function loadStageMatrices(stage) {
  let retval = false;
  try {
    let jsonObj = null;
    let upperTransformUrl = `${stage.crownCompositionUrl}/upper.transform.json`;
    jsonObj = await fetchJsonObjAsync(upperTransformUrl);
    stage.upperTransform = getMatrix4FromJsonObj(jsonObj);
    stage.invertedUpperTransform = stage.upperTransform
      ? stage.upperTransform.clone().invert()
      : null;

    let lowerTransformUrl = `${stage.crownCompositionUrl}/lower.transform.json`;
    jsonObj = await fetchJsonObjAsync(lowerTransformUrl);
    stage.lowerTransform = getMatrix4FromJsonObj(jsonObj);
    stage.invertedLowerTransform = stage.lowerTransform
      ? stage.lowerTransform.clone().invert()
      : null;

    if (stage.isOriginal) {
      let upperTeethMappingUrl = `${stage.crownCompositionUrl}/upper.roots_crowns_mapping.json`;
      stage.upperTeethMapping = stage.upperTransform
        ? await fetchJsonObjAsync(upperTeethMappingUrl)
        : null;

      let lowerTeethMappingUrl = `${stage.crownCompositionUrl}/lower.roots_crowns_mapping.json`;
      stage.lowerTeethMapping = stage.lowerTransform
        ? await fetchJsonObjAsync(lowerTeethMappingUrl)
        : null;
      retval =
        (stage.upperTransform || stage.lowerTransform) &&
        (stage.upperTeethMapping || stage.lowerTeethMapping);
    } else {
      let upperMovementUrl = `${stage.crownMovementUrl}/upper.movement.json`;
      stage.upperMovement = await fetchJsonObjAsync(upperMovementUrl);
      stage.upperMovementCommon = stage.upperMovement
        ? getMatrix4FromJsonObj(stage.upperMovement["common"])
        : null;
      stage.invertedUpperMovementCommon = stage.upperMovementCommon
        ? stage.upperMovementCommon.clone().invert()
        : null;

      let lowerMovementUrl = `${stage.crownMovementUrl}/lower.movement.json`;
      stage.lowerMovement = await fetchJsonObjAsync(lowerMovementUrl);
      stage.lowerMovementCommon = stage.lowerMovement
        ? getMatrix4FromJsonObj(stage.lowerMovement["common"])
        : null;
      stage.invertedLowerMovementCommon = stage.lowerMovementCommon
        ? stage.lowerMovementCommon.clone().invert()
        : null;

      retval =
        (stage.upperTransform || stage.lowerTransform) &&
        (stage.upperMovement || stage.lowerMovement);
    }

    console.log("loadStageMatrices", stage);
  } catch (error) {
    console.log("loadStageMatrices ERROR", error);
    retval = false;
  }
  return retval;
}

async function fetchJsonObjAsync(url) {
  let jsonObj = null;
  try {
    let response = await fetch(url);
    if (response.ok) {
      jsonObj = await response.json();
    } else {
      console.log("fetchJsonObjAsync ERROR response: ", response);
    }
  } catch (error) {
    console.log("fetchJsonObjAsync ERROR: ", error);
  }

  return jsonObj;
}

//#endregion

//#region load original geometry

async function loadOriginalStageModels() {
  let stageModels = [];
  let stage = vueApp.originalTreatmentStage;
  if (stage) {
    stage.isStitched = await checkStageIsStitched(stage);
    let originalUrl = stage.isStitched ? stage.stlCopyZip : stage.stlBaseZip;
    let originalZip = await loadJsZip(originalUrl);
    originalTeeth = await processZipToTeethArray(originalZip);
    stageModels = await getStageModelsFromZip(originalZip);

    // let stlCopyZip = stage.isStitched
    //   ? await loadJsZip(stage.stlCopyZip)
    //   : null;

    // if (stlCopyZip) {
    //   originalCbctTeeth = await processZipToTeethArray(stlCopyZip);
    //   originalStitchedTeeth = await processZipToTeethArray(stlBasezip);
    // } else {
    //   originalCbctTeeth = await processZipToTeethArray(stlBasezip);
    // }

    console.log("loadOriginalStageModels", stageModels, originalTeeth);
  }
  return stageModels;
}

async function processZipToTeethArray(zip) {
  let models = [];
  if (zip) {
    for (let [filename, zipFile] of Object.entries(zip.files)) {
      try {
        if (filename.startsWith("tooth_")) {
          let buffer = await zipFile.async("arraybuffer");
          let elementGeometry = zip.isGlb
            ? await getGeometryFromGlb(buffer)
            : getGeometryFromStl(buffer);
          let modelName = filename
            .replace("tooth_", "")
            .replace(".stl", "")
            .replace(".glb", "");
          filename = filename.replace(".glb", ".stl");
          let model = {
            name: modelName,
            filename: filename,
            geometry: elementGeometry,
          };
          models.push(model);
        }
      } catch (error) {
        console.log("processZipToTeethArray error for file", filename, error);
      }
    }
  }
  return models;
}

async function getStageModelsFromZip(zip) {
  let models = [];
  if (zip) {
    let allItems = vueApp.bones.concat(vueApp.nerves).concat(vueApp.airways);
    // let stage = vueApp.originalTreatmentStage;
    for (const item of allItems) {
      try {
        let fileName = item.filename;
        if (!isFalsyOrSpaces(fileName)) {
          if (zip.isGlb)
            fileName = fileName.replace(new RegExp(".stl$"), ".glb");
          let zipFile = zip.file(fileName);
          if (zipFile) {
            let buffer = await zipFile.async("arraybuffer");
            let elementGeometry = zip.isGlb
              ? await getGeometryFromGlb(buffer)
              : getGeometryFromStl(buffer);
            elementGeometry.computeBoundingBox();
            let phongMaterial = new THREE.MeshPhongMaterial({
              color: 0xeaeff2,
              flatShading: false,
              specular: 0x050505,
              shininess: 100,
              side: 2,
            });
            let tmesh = new THREE.Mesh(elementGeometry, phongMaterial);
            tmesh.filename = fileName;
            let model = {
              id: item.id,
              mesh: tmesh,
              color: item.color,
              opacity: item.opacity,
            };
            models.push(model);
          }
        }
      } catch (error) {
        console.log("getStageModelsFromZip error for item", item, error);
      }
    }
  }
  console.log("getStageModelsFromZip", models);
  return models;
}

async function checkStageIsStitched(stage) {
  try {
    let response = await fetch(stage.stlCopyZip, { method: "HEAD" });
    if (response.status === 200) {
      return true;
    }
  } catch (error) {
    console.log("checkIsStitched error", error);
  }
}

//#endregion

//#region UI

function switchTreatmentShowCollisions(newValue) {
  switchToggle("orthoTreatmentCollisionsDiv", newValue);
  treatmentContext.showRootCollisions = newValue;
  let isOriginal = currentStageId === 0;
  // vueApp.currentTreatmentStage === vueApp.originalTreatmentStage;

  if (newValue && !collisionsFound && !isOriginal) {
    showLoader(true);
    setTimeout(() => {
      findTeethCollisions();
      collisionsFound = true;
      hideLoader();
      switchTreatmentShowCollisions(newValue);
    }, 100);
    return;
  }

  let allTeeth = vueApp.isIntraoralView
    ? allModels.filter((x) => x.isTooth)
    : vueApp.allTeeth;

  allTeeth.forEach((tooth) => {
    let mesh = viewer.get_model_mesh(tooth.id);
    if (mesh) {
      setTreatmentMeshOpacity(tooth, mesh);
      if (mesh.crossedMeshes) {
        mesh.crossedMeshes.forEach((cmesh) => {
          cmesh.visible = !isOriginal && treatmentContext.showRootCollisions;
        });
      }
    }
  });
}

function setTreatmentMeshOpacity(tooth, mesh) {
  if (tooth && mesh) {
    let material = mesh.originalMaterial
      ? mesh.originalMaterial
      : mesh.material;
    material.opacity = treatmentContext.showRootCollisions ? 0.3 : 1;
  }
}

function enterPlaySmartRoots() {
  let currentStages = vueApp.treatmentStages;
  console.log("enterPlaySmartRoots", currentStages);

  initializeControls();
  updatePlaySlider(currentStages.length - 1, 0);

  vueApp.leaveModes(false, true, true);
  applyOriginalGeometries();

  // findTeethCollisions();
  // findTeethAnchorageLoss();

  vueApp.allItems.forEach((item) => {
    if (item.isGingiva) {
      item.isVisible = false;
      itemVisibilityChanged(item);
    }
  });

  $("#orthoTreatmentStagesDiv").css("display", "block");
  $("#orthoTreatmentDiv").css("display", "block");

  clearOrtoAccesories();
  vueApp.hideClipping();

  // $("#teethListBtn").css("display", "none");
  // $("#teethListContainer").css("display", "none");

  vueApp.isTreatmentPlayMode = true;

  if (treatmentContext.showOriginal) {
    vueApp.allTeeth.forEach((item) => {
      let mesh = viewer.get_model_mesh(item.id);
      if (mesh.originalTreatmentMesh) {
        mesh.originalTreatmentMesh.isVisible = true;
        mesh.originalTreatmentMesh.visible = item.isVisible && mesh.visible;
      }
    });
  }
}

function applyOriginalGeometries() {
  if (vueApp.isStitched) {
    vueApp.viewSourceType = "cbct";
    switchStitching(vueApp.viewSourceType);
  }
  vueApp.allTeeth.forEach((tooth) => {
    let mesh = viewer.get_model_mesh(tooth.id);
    if (mesh) {
      if (!mesh.originalTreatmentGeometry) {
        let centerMatrix = globalCenterMatrix.clone();
        let originalTooth = originalTeeth
          ? originalTeeth.find((t) => t.filename === tooth.filename)
          : null;
        if (originalTooth) {
          let geometry = originalTooth.geometry;
          // geometry.applyMatrix4(globalCenterMatrix);
          let cgeometry = geometry.clone();
          cgeometry.applyMatrix4(globalCenterMatrix);
          cgeometry.computeBoundingBox();

          // let cbox = offsetBox(cgeometry.boundingBox);
          centerMatrix.setPosition(
            globalCenterMesh.position.x - mesh.defaultPosition.x,
            globalCenterMesh.position.y - mesh.defaultPosition.y,
            globalCenterMesh.position.z - mesh.defaultPosition.z
          );
          geometry.applyMatrix4(centerMatrix);

          geometry = new THREE.Geometry().fromBufferGeometry(geometry);
          geometry.mergeVertices();
          geometry.computeVertexNormals();
          geometry = new THREE.BufferGeometry().fromGeometry(geometry);
          geometry.computeBoundingBox();

          mesh.originalTreatmentGeometry = geometry;
        } else {
          console.log("!!! NO originalTooth");
          mesh.originalTreatmentGeometry = mesh.geometry;
          // mesh.originalTreatmentGeometry = mesh.cbctGeometry
          //   ? mesh.cbctGeometry
          //   : mesh.geometry;
          centerMatrix.setPosition(
            globalCenterMesh.position.x - mesh.position.x,
            globalCenterMesh.position.y - mesh.position.y,
            globalCenterMesh.position.z - mesh.position.z
          );
        }
        mesh.originalTreatmentMatrix = centerMatrix;
        mesh.invertedTreatmentMatrix = centerMatrix.clone().invert();
      }

      mesh.geometry = mesh.originalTreatmentGeometry;

      mesh.position.set(
        // 0,
        // 0,
        // 0
        mesh.defaultPosition.x,
        mesh.defaultPosition.y,
        mesh.defaultPosition.z
      );
      mesh.scale.set(1, 1, 1);
      mesh.rotation.set(0, 0, 0);

      if (!mesh.originalTreatmentMesh) {
        let clone_material = mesh.material.clone();
        clone_material.color.set(original_teeth_color);
        clone_material.opacity = original_teeth_opacity;
        clone_material.transparent = true;

        mesh.originalTreatmentMesh = new THREE.Mesh(
          mesh.originalTreatmentGeometry.clone(),
          clone_material
        );

        mesh.originalTreatmentMesh.position.copy(mesh.position);
        viewer.scene.add(mesh.originalTreatmentMesh);

        mesh.originalTreatmentMesh.visible =
          mesh.originalTreatmentMesh.isVisible = false;
      }
    }
  });
}

function restoreDefaultGeometries() {
  applyTreatmentStage(0);
  if (vueApp.isStitched) {
    switchStitching(vueApp.viewSourceType);
  } else {
    vueApp.allTeeth.forEach((tooth) => {
      let mesh = viewer.get_model_mesh(tooth.id);
      if (mesh) {
        mesh.geometry = mesh.cbctGeometry;
      }
    });
  }

  vueApp.allItems.forEach((item) => {
    let mesh = viewer.get_model_mesh(item.id);
    if (mesh && mesh.originalTreatmentMesh) {
      mesh.originalTreatmentMesh.isVisible =
        mesh.originalTreatmentMesh.visible = false;
    }

    if (!item.isTooth) {
      // item.isVisible = !item.isHidden;
      itemVisibilityChanged(item);
    }
  });

  clearOrtoAccesories();
  vueApp.restoreClipping();
}

function switchTreatmentPlay(isPlaying) {
  if (vueApp.isTreatmentPlaying != isPlaying) {
    vueApp.isTreatmentPlaying = isPlaying;
    if (vueApp.isTreatmentPlaying) {
      currentStageId = 0;
      updateSliderValue(currentStageId);
      applyTreatmentStage(currentStageId);
      playNextStage();
    }
  }
}

function applyTreatmentStage(stageId, needAnimation) {
  console.log("applyTreatmentStage", stageId);

  let stage = vueApp.treatmentStages.find((x) => x.id === stageId);
  let originalStage = vueApp.originalTreatmentStage;

  if (stage && originalStage) {
    currentStageId = stageId;
    updateSliderValue(currentStageId);

    vueApp.allTeeth.forEach((tooth) => {
      let mesh = viewer.get_model_mesh(tooth.id);
      if (mesh && mesh.originalTreatmentGeometry) {
        // mesh.geometry = mesh.originalTreatmentGeometry;
        if (stage != vueApp.originalTreatmentStage) {
          let originalTransformMatrix = tooth.isUpper
            ? originalStage.upperTransform
            : originalStage.lowerTransform;
          let invertedOriginalTransformMatrix = tooth.isUpper
            ? originalStage.invertedUpperTransform
            : originalStage.invertedLowerTransform;

          let commonMovementMatrix = tooth.isUpper
            ? stage.upperMovementCommon
            : stage.lowerMovementCommon;
          let invertedCommonMovementMatrix = tooth.isUpper
            ? stage.invertedUpperMovementCommon
            : stage.invertedLowerMovementCommon;

          let rootsCrownsMapping = tooth.isUpper
            ? originalStage.upperTeethMapping
            : originalStage.lowerTeethMapping;
          let movementJson = tooth.isUpper
            ? stage.upperMovement
            : stage.lowerMovement;

          let crownNumber = rootsCrownsMapping
            ? rootsCrownsMapping[tooth.number]
            : null;
          let crownJson =
            crownNumber && movementJson ? movementJson[crownNumber] : null;
          let toothMovementMatrix = getMatrix4FromJsonObj(crownJson);
          // getMatrix4FromJsonObj(crownJson);
          if (
            mesh.invertedTreatmentMatrix &&
            originalTransformMatrix &&
            commonMovementMatrix &&
            toothMovementMatrix &&
            invertedOriginalTransformMatrix &&
            mesh.originalTreatmentMatrix
          ) {
            let multiMatrix = new THREE.Matrix4();
            multiMatrix.multiplyMatrices(
              mesh.invertedTreatmentMatrix,
              multiMatrix
            );

            multiMatrix.multiplyMatrices(originalTransformMatrix, multiMatrix);
            multiMatrix.multiplyMatrices(commonMovementMatrix, multiMatrix);
            multiMatrix.multiplyMatrices(toothMovementMatrix, multiMatrix);
            multiMatrix.multiplyMatrices(
              invertedCommonMovementMatrix,
              multiMatrix
            );
            multiMatrix.multiplyMatrices(
              invertedOriginalTransformMatrix,
              multiMatrix
            );

            multiMatrix.multiplyMatrices(
              mesh.originalTreatmentMatrix,
              multiMatrix
            );

            let tempMesh = new THREE.Mesh();
            tempMesh.applyMatrix4(new THREE.Matrix4());
            tempMesh.applyMatrix4(multiMatrix);
            tempMesh.position.set(
              mesh.defaultPosition.x + tempMesh.position.x,
              mesh.defaultPosition.y + tempMesh.position.y,
              mesh.defaultPosition.z + tempMesh.position.z
            );

            if (needAnimation) {
              viewer.animate_model(tooth.id, {
                exact: {
                  x: tempMesh.position.x,
                  y: tempMesh.position.y,
                  z: tempMesh.position.z,
                  rotationx: tempMesh.rotation.x,
                  rotationy: tempMesh.rotation.y,
                  rotationz: tempMesh.rotation.z,
                  // scalex: tempMesh.scale.x,
                  // scaley: tempMesh.scale.y,
                  // scalez: tempMesh.scale.z,
                  msec: ANIMATE_STAGE_MS - 50,
                },
              });
            } else {
              mesh.position.set(
                tempMesh.position.x,
                tempMesh.position.y,
                tempMesh.position.z
              );
              // mesh.scale.set(
              //   tempMesh.scale.x,
              //   tempMesh.scale.y,
              //   tempMesh.scale.z
              // );
              mesh.rotation.set(
                tempMesh.rotation.x,
                tempMesh.rotation.y,
                tempMesh.rotation.z
              );
              mesh.updateMatrix();
            }
          }
        } else {
          mesh.position.set(
            mesh.defaultPosition.x,
            mesh.defaultPosition.y,
            mesh.defaultPosition.z
          );
          mesh.scale.set(1, 1, 1);
          mesh.rotation.set(0, 0, 0);
          mesh.updateMatrix();
        }
      }
    });

    if (needAnimation) {
      setTimeout(() => {
        // findTeethCollisions();
        playNextStage();
        switchTreatmentShowCollisions(treatmentContext.showRootCollisions);
      }, ANIMATE_STAGE_MS);
    } else {
      // findTeethCollisions();
      switchTreatmentShowCollisions(treatmentContext.showRootCollisions);
    }
  } else {
    vueApp.isTreatmentPlaying = false;
  }
}

function playNextStage() {
  if (playInterval) {
    clearInterval(playInterval);
    playInterval = null;
  }

  if (vueApp.isTreatmentPlaying) {
    let maxId = vueApp.treatmentStages.length - 1;
    let stageId = currentStageId;
    let nextStageId = stageId + 1;

    if (nextStageId <= maxId) {
      applyTreatmentStage(nextStageId, true);
      // updateSliderValue(stageId);
      let counter = 0;
      playInterval = setInterval(function () {
        ++counter;
        let newValue = stageId + counter / 100;
        if (newValue <= maxId) {
          updateSliderValue(newValue);
        } else {
          if (vueApp.isTreatmentPlaying) updateSliderValue(maxId);
          clearInterval(playInterval);
          playInterval = null;
        }
      }, ANIMATE_STAGE_MS / 100);
    } else if (vueApp.isTreatmentPlaying) {
      // applyTreatmentStage(maxId, false, false);
      updateSliderValue(maxId);
      vueApp.isTreatmentPlaying = false;
    }
  }
}

//#region Slider

function updatePlaySlider(max, value) {
  if (!playInitialized) {
    initMovementVector(
      playSliderChanged,
      "#playTreatmentSlider",
      true, // horizontal
      0, // min
      max,
      0.01, // step
      value
    );
    playInitialized = true;
  } else {
    // if (value > 0) {
    //   console.log("#playTreatmentSlider", max, value);
    // }
    updateSlider("#playTreatmentSlider", max, value);
  }
}

function playSliderChanged(value) {
  let newValue = Math.floor(value); //Math.floor(value); Math.round(value);
  console.log("playSliderChanged", value, newValue);
  // newValue = newValue == -1 ? -1 : newValue + 1;
  if (currentStageId != newValue && !vueApp.isTreatmentPlaying) {
    currentStageId = newValue;
    applyTreatmentStage(currentStageId);
  }
}

function updateSliderValue(stageId) {
  if (stageId !== undefined) {
    updatePlaySlider(vueApp.treatmentStages.length - 1, stageId);
  }
}
//#endregion

//#endregion

//#region Math

function getMappedKey(jsonObj, value) {
  for (const key in jsonObj) {
    if (Object.hasOwnProperty.call(jsonObj, key)) {
      const element = jsonObj[key];
      if (element == value) {
        return key;
      }
    }
  }
}

function getMatrix4FromJsonObj(jsonObj) {
  let matrix4 = null;
  if (!jsonObj) return matrix4;
  try {
    const marray = jsonObj.transform ? jsonObj.transform : jsonObj;
    const array1 = marray[0];
    const array2 = marray[1];
    const array3 = marray[2];
    const array4 = marray[3];

    const n11 = array1[0];
    const n12 = array1[1];
    const n13 = array1[2];
    const n14 = array1[3];

    const n21 = array2[0];
    const n22 = array2[1];
    const n23 = array2[2];
    const n24 = array2[3];

    const n31 = array3[0];
    const n32 = array3[1];
    const n33 = array3[2];
    const n34 = array3[3];

    const n41 = array4[0];
    const n42 = array4[1];
    const n43 = array4[2];
    const n44 = array4[3];

    matrix4 = new THREE.Matrix4();
    matrix4.set(
      n11,
      n12,
      n13,
      n14,
      n21,
      n22,
      n23,
      n24,
      n31,
      n32,
      n33,
      n34,
      n41,
      n42,
      n43,
      n44
    );
  } catch (error) {
    console.log("getMatrix4FromJsonObj ERROR: ", jsonObj, error);
  }
  return matrix4;
}

//#endregion

export {
  enterPlaySmartRoots,
  fetchTreatmentStages,
  restoreDefaultGeometries,
  switchTreatmentPlay,
  applyTreatmentStage,
  switchTreatmentShowCollisions,
};
